/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { default_theme as theme } from '../emotion/theme'
import { jsx, css } from '@emotion/react'
import { head, get } from 'lodash'
import { Link } from 'react-router-dom'
import { CardHeader } from './layout/CardHeader'
import { Modal } from 'react-bootstrap'
import { Trans, Translation } from 'react-i18next'
import BusyMask from './BusyMask'
import CurrencyValue from './CurrencyValue'
import Timestamp from './Timestamp'
import Card from './layout/Card'
import CardInfoText from './layout/CardInfoText'
import CardH2Text from './layout/CardH2Text'
import CommonTable from './CommonTable'
import { InlineIcon } from './layout/InlineIcon'
import { WhiteButton } from './layout/WhiteButton'
import MainLayout from './MainLayout'
import QuickLinksCard from './QuickLinksCard'
import CreditInformationCard from './CreditInformationCard'
import { Row, Col, Container } from 'react-bootstrap'
import { Separator } from './layout/Separator'
import { DatePicker } from './form/DatetimePicker'
import TableFilter from './layout/TableFilter'
import { Button } from 'react-bootstrap'
import { aggregatedTransactions } from '../actions/aggregated_transactions'

export const TransactionsTable = ({transactionList, showBalance, showingCreditsPerspective}) => {

    const [showingTransactionDetails, setShowingTransactionDetails] = useState(null)
    const dispatch = useDispatch()
    const transactions = useSelector(() => transactionList.getVisibleObjects())
    const is_loading = useSelector(() =>  transactionList.isLoading())
    const filter = useSelector(() => transactionList.getFilter())

    let headers = {}
    if ( showBalance ) {
        headers = {
            short_ref: { name: <Trans>Id</Trans> },
            customer_credit_amount_excluding_vat_euros: {
                name: <Trans>Amount</Trans>,
            },
            executed_at: { name: <Trans>When</Trans> },
            description: { name: <Trans>Description</Trans> },
            credits_balance: { name: <Trans>Balance</Trans> },  
            action: { name: '' },
        }
    } else {
        headers = {
            short_ref: { name: <Trans>Id</Trans> },
            customer_credit_amount_excluding_vat_euros: {
                name: <Trans>Amount</Trans>,
            },
            executed_at: { name: <Trans>When</Trans> },
            description: { name: <Trans>Description</Trans> },
            action: { name: '' },
        }
    }
    

    useEffect(() => {
        dispatch(transactionList.fetchListIfNeeded())
    })

    const onViewTransaction = (evt, item) => {
        setShowingTransactionDetails(item)
    }

    const onHideTransaction = () => {
        setShowingTransactionDetails(null)
    }

    const onFilterChanged = (filter_values) => {
        dispatch(transactionList.updateListFilter(filter_values))
    }

    const onDownload = () => {
        transactionList.download(filter)
    }

    const getCellValue = (header_key, item, index) => {
        switch (header_key) {
            case 'description':
                return (
                    <>
                      {item.description}
                      { item.transaction_type === 'subscription_reactivation_payment' &&
                        <span css={description_sub_field_style}>
                          &nbsp;(<Trans>Reactivation</Trans>)
                        </span>
                      }
                    </>
                )
            case 'executed_at':
                return <Timestamp value={item.executed_at} format="datetime" />
                
            case 'customer_credit_amount_excluding_vat_euros':
                return (
                    <CurrencyValue
                        value={showingCreditsPerspective ? item.customer_credit_amount_excluding_vat_euros : item.customer_subscription_amount_excluding_vat_euros }
                    />
                )
            case 'credits_balance':
                if ( item.affects_credit_balance ) {
                    return (
                        <CurrencyValue value={item.credits_balance_excluding_vat_euros} />
                    )
                } else {
                    return null
                }
            case 'action':
                return (
                    <InlineIcon
                        icon_name="eye"
                        onClick={(evt) => onViewTransaction(evt, item)}
                    />
                )
            default:
                return undefined
        }
    }

    const renderTransactionDetailsModal = (transaction) => {
        return (
            <Modal show={true} size="lg" onHide={onHideTransaction}>
              <Modal.Header closeButton>
                <Modal.Title>
                  <Trans>Transaction details</Trans>
                </Modal.Title>
              </Modal.Header>

              <Modal.Body>
                <div>
                  <Row>
                    <Col>
                      <Trans>Xoip Id</Trans>
                    </Col>
                    <Col>{transaction.short_ref}</Col>
                  </Row>
                  <Row>
                    <Col>
                      <Trans>Reference</Trans>
                    </Col>
                    <Col>{transaction.external_ref}</Col>
                  </Row>
                  <Row>
                    <Col>
                      <Trans>Executed at</Trans>
                    </Col>
                    <Col>
                      <Timestamp value={transaction.executed_at} format="datetime" />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Trans>Description</Trans>
                    </Col>
                    <Col>
                      {transaction.description}
                      { transaction.transaction_type === 'subscription_reactivation_payment' &&
                        <span css={description_sub_field_style}>
                          &nbsp;(<Trans>Reactivation</Trans>)
                        </span>
                      }
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Trans>Payment system</Trans>
                    </Col>
                    <Col>{transaction.payment_system}</Col>
                  </Row>
                  {transaction.vat_percentage > 0 && (
                      <div>
                        <Row>
                          <Col>
                            <Trans>Amount ex VAT</Trans>
                          </Col>
                          <Col css={transaction_modal_currency_value_row_style}>
                            <CurrencyValue
                                value={transaction.amount_excluding_vat_euros}
                            />
                          </Col>
                        </Row>
                        <Row>
                          <Col>
                            <Trans>VAT</Trans>
                          </Col>
                          <Col css={transaction_modal_currency_value_row_style}>
                            {transaction.vat_percentage}%
                          </Col>
                        </Row>
                        <Row>
                          <Col>
                            <Trans>Amount inc. VAT</Trans>
                          </Col>
                          <Col css={transaction_modal_currency_value_row_style}>
                            <CurrencyValue
                                value={transaction.amount_including_vat_euros}
                            />
                          </Col>
                        </Row>
                      </div>
                  )}
                  {transaction.vat_percentage === 0 && (
                      <Row>
                        <Col>
                          <Trans>Amount</Trans>
                        </Col>
                        <Col css={transaction_modal_currency_value_row_style}>
                          <CurrencyValue
                              value={transaction.amount_excluding_vat_euros}
                          />
                        </Col>
                      </Row>
                  )}
                  {false && (
                      <Row>
                        <Col>
                          <Trans>From</Trans>
                        </Col>
                        <Col>{transaction.debtor_display_name}</Col>
                      </Row>
                  )}
                  <Row>
                    <Col>
                      <Trans>To</Trans>
                    </Col>
                    <Col>{transaction.creditor_display_name}</Col>
                  </Row>
                </div>
              </Modal.Body>
            </Modal>
        )
    }

    return (
        <>
          <Separator variant="h20" />
          {showingTransactionDetails &&
           renderTransactionDetailsModal(showingTransactionDetails)
          }

          <CommonTable
              is_loading={is_loading}
              empty_message={<Trans>There are no transactions.</Trans>}
              headers={headers}
              items={transactions}
              item_list={transactionList}
              getCellValue={getCellValue}
          />
        </>
    )

}

const transaction_modal_currency_value_row_style = css`
  display: flex;
`

const header_right_child = css`
  display: flex;
  align-items: center;
`

const download_button_style = css`
  display: flex;
  justify-content: space-between;
`

const total_amounts_container_style = css``

const description_sub_field_style = css`
color: ${theme.colors.grey};
`
